import { Classes, Collapse, CollapseProps } from "@blueprintjs/core";
import React, { useCallback, useMemo, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import {
  SingleChevronDownIcon,
  SingleChevronDownSmallIcon,
  SingleChevronRightIcon,
  SingleChevronRightSmallIcon,
} from "../components/icons/CustomIcons";
import { useToggleState } from "../hooks/useToggleState";

import { HexClasses } from "./classes.js";

export interface HexCollapseProps extends Pick<CollapseProps, "children"> {
  title: string | React.ReactNode;
  icon?: React.ReactNode;
  rightElement?: React.ReactNode;
  togglePosition?: "left" | "right" | "inline" | "none";
  /** Keep the toggle's layout space reserved, but reveal it on hover or focus. */
  showToggleOnHoverWhenClosed?: boolean;
  /** Rendered after an inline toggle and revealed on hover or focus, for a row's secondary action. */
  inlineAccessory?: React.ReactNode;
  duration?: "default" | "medium" | "large" | "none";
  /**
   * Used to override the toggle function and control state externally
   */
  isOpen?: boolean;
  isOpenDefault?: boolean;
  toggleIsOpen?: () => void;
  /**
   * If true, the toggle will be disabled and the toggle icon will be hidden.
   */
  disabled?: boolean;
  keepChildrenMounted?: boolean;
  /**
   * A callback fired when the header is clicked outside of the icon. The
   * default behavior is to toggle the collapse (cancelable by
   * `event.preventDefault()`).
   */
  onHeaderClick?: (event: React.MouseEvent) => void;
  /**
   * A callback fired when the header is keyed down outside of the icon. The
   * default behavior on Space or Enter is to simulate a click on the header
   * (cancelable by `event.preventDefault()`).
   */
  onHeaderKeyDown?: (event: React.KeyboardEvent) => void;
  className?: string;
  headerStyle?: React.CSSProperties;
  "data-test"?: string;

  /**
   * If true, use the small chevron icon
   */
  small?: boolean;
}

export const HexCollapse = React.memo(
  ({
    children,
    className,
    "data-test": dataTest,
    disabled = false,
    duration = "default",
    headerStyle,
    icon,
    inlineAccessory,
    isOpen: isOpenOverride,
    isOpenDefault = true,
    keepChildrenMounted = false,
    onHeaderClick: onHeaderClickProp,
    onHeaderKeyDown: onHeaderKeyDownProp,
    ref,
    rightElement,
    showToggleOnHoverWhenClosed = false,
    small,
    title,
    toggleIsOpen: toggleOverride,
    togglePosition = "left",
  }: HexCollapseProps & { ref?: React.Ref<HTMLDivElement> }) => {
    const theme = useTheme();
    const [isOpen, _, { toggle: toggleIsOpen }] = useToggleState(isOpenDefault);
    const [isToggleVisible, setIsToggleVisible] = useState(false);
    const tracksHover =
      showToggleOnHoverWhenClosed ||
      (togglePosition === "inline" && inlineAccessory != null);

    const resolvedIsOpen = isOpenOverride ?? isOpen;
    const resolvedToggle = toggleOverride ?? toggleIsOpen;

    const animationDuration = useMemo(() => {
      if (duration === "medium") {
        return theme.animation.durationMediumMs;
      } else if (duration === "large") {
        return theme.animation.durationLargeMs;
      } else if (duration === "none") {
        return 0;
      }
      return theme.animation.durationMs;
    }, [theme.animation, duration]);

    const toggleHandler = useCallback(
      (evt: React.MouseEvent) => {
        evt.stopPropagation();
        resolvedToggle();
      },
      [resolvedToggle],
    );

    const toggleIcon = useMemo(() => {
      const OpenIcon = small
        ? SingleChevronRightSmallIcon
        : SingleChevronRightIcon;
      const CloseIcon = small
        ? SingleChevronDownSmallIcon
        : SingleChevronDownIcon;

      return disabled ? null : resolvedIsOpen ? (
        <CloseIcon data-hex-collapse-toggle="" onClick={toggleHandler} />
      ) : (
        <OpenIcon data-hex-collapse-toggle="" onClick={toggleHandler} />
      );
    }, [disabled, resolvedIsOpen, toggleHandler, small]);

    const renderedToggleIcon = showToggleOnHoverWhenClosed ? (
      <span
        css={css`
          display: inline-flex;
          flex-shrink: 0;
          opacity: ${resolvedIsOpen || isToggleVisible ? 1 : 0};
          transition: opacity ${theme.animation.duration}
            ${theme.animation.easing};
        `}
      >
        {toggleIcon}
      </span>
    ) : (
      toggleIcon
    );

    const handleHeaderClick = useCallback<React.MouseEventHandler>(
      (event) => {
        onHeaderClickProp?.(event);
        if (event.defaultPrevented) return;
        if (disabled) return;
        resolvedToggle();
      },
      [onHeaderClickProp, resolvedToggle, disabled],
    );

    const handleHeaderKeyDown = useCallback<React.KeyboardEventHandler>(
      (event) => {
        onHeaderKeyDownProp?.(event);
        if (event.defaultPrevented) return;
        if (event.key === "Enter" || event.key === " ") {
          const mouseEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
          });
          event.target.dispatchEvent(mouseEvent);
        }
      },
      [onHeaderKeyDownProp],
    );

    return (
      <Wrapper ref={ref} $duration={animationDuration} className={className}>
        <ToggleHeader
          style={headerStyle}
          aria-disabled={disabled}
          aria-expanded={resolvedIsOpen}
          data-test={dataTest}
          role="button"
          tabIndex={0}
          onClick={handleHeaderClick}
          onBlur={tracksHover ? () => setIsToggleVisible(false) : undefined}
          onFocus={tracksHover ? () => setIsToggleVisible(true) : undefined}
          onKeyDown={handleHeaderKeyDown}
          onMouseEnter={
            tracksHover ? () => setIsToggleVisible(true) : undefined
          }
          onMouseLeave={
            tracksHover ? () => setIsToggleVisible(false) : undefined
          }
        >
          <ToggleWrapper>
            {togglePosition === "left" && renderedToggleIcon}
            <HexCollapseHeader>
              {icon}
              {title}
              {togglePosition === "inline" && (
                <span css="display: inline-flex; flex-shrink: 0; margin-left: 3px;">
                  {renderedToggleIcon}
                </span>
              )}
              {togglePosition === "inline" && inlineAccessory != null && (
                <span
                  css={css`
                    display: inline-flex;
                    flex-shrink: 0;
                    margin-left: 4px;
                    opacity: ${isToggleVisible ? 1 : 0};
                    transition: opacity ${theme.animation.duration}
                      ${theme.animation.easing};
                  `}
                >
                  {inlineAccessory}
                </span>
              )}
            </HexCollapseHeader>
          </ToggleWrapper>
          <ToggleWrapper>
            {rightElement ? <div>{rightElement}</div> : null}
            {togglePosition === "right" && renderedToggleIcon}
          </ToggleWrapper>
        </ToggleHeader>

        {/*
          BP's Collapse doesn't forward props to the underlying element,
          so we can't set any of the necessary attributes for accessibility
          (AccordionPanel: `id`, `role`, `aria-labelledby`)
          (`id` is necessary for AccordionHeader's `aria-controls`)
        */}
        <Collapse
          isOpen={resolvedIsOpen}
          keepChildrenMounted={keepChildrenMounted}
          transitionDuration={animationDuration}
        >
          {children}
        </Collapse>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div<{ $duration: number }>`
  .${Classes.COLLAPSE} {
    transition: height ${({ $duration }) => `${$duration}ms`};
    ${({ theme }) => theme.animation.easing};

    .${Classes.COLLAPSE_BODY} {
      transform: none !important;
    }
  }
`;

const ToggleHeader = styled.div.attrs({
  className: HexClasses.HEX_COLLAPSE_TOGGLE_HEADER,
})`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  padding: 15px 10px 5px 0;

  svg {
    color: ${({ theme }) => theme.iconColor};
  }

  &:hover:not([aria-disabled="true"]) {
    cursor: pointer;
  }
  &[role="button"][aria-disabled="true"] {
    /* overrides a broader role="button" cursor style */
    cursor: default;
  }
`;

const ToggleWrapper = styled.div.attrs({
  className: HexClasses.HEX_COLLAPSE_TOGGLE_WRAPPER,
})`
  display: flex;
  gap: 3px;
  align-items: center;
`;

const HexCollapseHeader = styled.div.attrs({
  className: HexClasses.HEX_COLLAPSE_HEADER,
})`
  display: flex;
  align-items: center;

  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
  font-size: ${({ theme }) => theme.fontSize.DEFAULT};
  user-select: none;

  .${Classes.ICON} {
    margin-right: 6px;
  }
`;
